import React, { useRef, useEffect, useState, useMemo } from "react";

import { Icon } from "components/Icon";
import { Alert } from "services/Alert";

import ReactDOM from "react-dom";
import styled, { css } from "styled-components";

import { generateId, compareObjects, formValid, getManagedServices, getFormattedDatetime } from "utils";
import { checkPermissionsFor } from "store/storeHelpers";
import { useWindowWidth } from "hooks";
import { hasErrorObject } from "utils";
import { api } from "api/loginRoutes";
import {
    ACCOUNT_ALLOCATION_BAN_CLEAN_API, SMS_SUPPLIER_MODIFY_METHOD, ACCOUNT_MODIFY_METHOD
} from "const/apiMethods";
import { ButtonContent, ButtonListAdd, ButtonPrimary, ButtonClose, ButtonSecondary } from "components/base/BaseButton";
import ControlledCheckbox from "components/Form/ControlledCheckbox";
import ModalConfirm from "components/Modal/ModalConfirm";
import Contacts, { contactModel, supplierContactsModel, telegramModel } from "./Contacts";
import Users, { initUser, userModel } from "./Customers";
import MergeAccountsModal from "./MergeAccountsModal";
import { AccountCreate } from "./SaveModel";
import ServiceTable from "./ServiceTable";
import CustomField from "./CustomFieldRefactored";
import "./CreateForm.css";

import { Button, ButtonGroup, ButtonToolbar, Checkbox, FlexboxGrid, Input, Popover, Schema, SelectPicker } from "rsuite";
import Whisper from "components/Whisper";
import Form from "components/Form/CompatForm";

const { StringType, NumberType } = Schema.Types;

const accountModelCreate = Schema.Model({
    user_name: StringType().isRequired("Required").maxLength(40, "The maximum is only 40 characters."),
    user_email: StringType().isRequired("Required").maxLength(40, "The maximum is only 40 characters.").isEmail("Email required"),
    telegram_username: StringType()
        .pattern(/.*\B@(?=\w{5,32}\b)[a-zA-Z0-9]+(?:_[a-zA-Z0-9]+)*.*/, "Telegram username is incorrect. Telegram username must be 5-32 long and contain the '@' symbol at the beginning."),
    name: StringType().isRequired("Required")
        .pattern(/^([a-zA-Z0-9.,()_\-+]+ )*[a-zA-Z0-9.,()_\-+]+ ?$/, "You can type only letters, numbers and '.,()_-+' special characters")
        .maxLength(40, "The maximum is only 40 characters."),
    // account_manager_id: StringType().isRequired("Required"),
    nickname: StringType().isRequired("Required")
        .pattern(/^([a-zA-Z0-9.,()_\-+]+ )*[a-zA-Z0-9.,()_\-+]+ ?$/, "You can type only letters, numbers and '.,()_-+' special characters")
        .maxLength(40, "The maximum is only 40 characters."),
    user_teams_email: StringType()
        .isRequired("Required")
        .maxLength(256, "The maximum is only 256 characters.")
        .pattern(/^([a-zA-Z0-9_+.-])+@(([a-zA-Z0-9-])+\.)+([a-zA-Z0-9]{2,4})+$/, "Incorrect email address"),
    user_teams_link: StringType()
        .isRequired("Required")
        .maxLength(256, "The maximum is only 256 characters.")
        .pattern(/^(https?:\/\/)([a-z0-9-]+\.)+[a-z]{2,}(:[0-9]{1,5})?(\/.*)?$/i, "Incorrect teams link"),
    cur_key: NumberType().isRequired("Required"),
    pt_key: NumberType().isRequired("Required"),
    account_manager_id: StringType().isRequired("Required")
});

const callDurationModel = Schema.Model({
    call_duration_max: NumberType().min(1, "Enter a positive number").max(10800, "3 hours is maximum").isRequired("Required")
});

const accountModelModify = Schema.Model({
    name: StringType().isRequired("Required")
        .pattern(/^([a-zA-Z0-9.,()_\-+]+ )*[a-zA-Z0-9.,()_\-+]+ ?$/, "You can type only letters, numbers and '.,()_-+' special characters")
        .maxLength(40, "The maximum is only 40 characters."),
    nickname: StringType().isRequired("Required")
        .pattern(/^([a-zA-Z0-9.,()_\-+]+ )*[a-zA-Z0-9.,()_\-+]+ ?$/, "You can type only letters, numbers and '.,()_-+' special characters")
        .maxLength(40, "The maximum is only 40 characters."),
    // account_manager_id: StringType().isRequired("Required"),
    cur_key: NumberType().isRequired("Required"),
    pt_key: NumberType().isRequired("Required"),
});

const supplierModelCreate = Schema.Model({
    user_name: StringType().isRequired("Required").maxLength(40, "The maximum is only 40 characters."),
    user_email: StringType().isRequired("Required").maxLength(40, "The maximum is only 40 characters.").isEmail("Email required"),
    telegram_username: StringType()
        .pattern(/.*\B@(?=\w{5,32}\b)[a-zA-Z0-9]+(?:_[a-zA-Z0-9]+)*.*/, "Telegram username is incorrect. Telegram username must be 5-32 long and contain the '@' symbol at the beginning."),
    name: StringType().isRequired("Required")
        .pattern(/^([a-zA-Z0-9.,()_\-+]+ )*[a-zA-Z0-9.,()_\-+]+ ?$/, "You can type only letters, numbers and '.,()_-+' special characters")
        .maxLength(40, "The maximum is only 40 characters."),
    user_teams_email: StringType()
        .maxLength(256, "The maximum is only 256 characters.")
        .pattern(/^([a-zA-Z0-9_+.-])+@(([a-zA-Z0-9-])+\.)+([a-zA-Z0-9]{2,4})+$/, "Incorrect email address"),
    user_teams_link: StringType()
        .maxLength(256, "The maximum is only 256 characters.")
        .pattern(/^(https?:\/\/)([a-z0-9-]+\.)+[a-z]{2,}(:[0-9]{1,5})?(\/.*)?$/i, "Incorrect teams link"),
    // account_manager_id: StringType().isRequired("Required"),
    cur_key: NumberType().isRequired("Required"),
    pt_key: NumberType().isRequired("Required"),
    supplier_credit_limit: NumberType().min(1, "Enter a positive number"),
    account_manager_id: StringType().isRequired("Required")
});

const supplierModelModify = Schema.Model({
    name: StringType().isRequired("Required")
        .pattern(/^([a-zA-Z0-9.,()_\-+]+ )*[a-zA-Z0-9.,()_\-+]+ ?$/, "You can type only letters, numbers and '.,()_-+' special characters")
        .maxLength(40, "The maximum is only 40 characters."),
    // account_manager_id: StringType().isRequired("Required"),
    pt_key: NumberType().isRequired("Required"),
    supplier_credit_limit: NumberType().min(1, "Enter a positive number")
});

const serviceTableModel = Schema.Model({
    account_manager_id: StringType().isRequired("Required"),
    cur_key: NumberType().isRequired("Required"),
    pt_key: NumberType().isRequired("Required"),
});

const styleRow = { margin: "10px 0px" };

const serviceListData = [
    {
        name: "Voice",
        id: "voice"
    },
    {
        name: "SMS",
        id: "sms"
    }
];

// const UNEDITABLE = 0;
// const CREATING_CODE = 1;
// const EDITING_CODE = 2;


export default ({
    disabled, disabledCurrency = false, onSubmit: _onSubmit, currency_list, payment_terms_list,
    account_manager_list, formDefaultValue, accountId, onCancel, active, getAccounts, client_role_list, admin_role_list,
    isTestAccount, isSeparatedPage, registration_key, checkDuplicates, onSetRedirectTab, location, history, duplicateContacts,
    countryList, howFindList, newAccount, permissions, accountJoinList, joinLoading, getAccountJoin, service, setService, authInfo,
    newSupplier, isSupport, isSupplier, isCreateJoined, isSupervisorSms, currentSupplier, supplierTrunks, setResellerMode, getRegistrationCount,
    allocationBanExpiredDate = null, generatedNameList = [], getAccountManagersDropdownList,
    ...props
}) => {

    let currentModel;
    const didFetchManagers = useRef(null);

    const managedContactModel = useMemo(() => {
        return isSupplier ? supplierContactsModel : Schema.Model.combine(contactModel, telegramModel)
    }, [isSupplier]);

    const initContact = {
        person: "",
        email: "",
        phone: "",
        teams_email: "",
        teams_link: null,
        ...(!isSupplier ? { telegram_username: "" } : {}),
        role_list: [],
        key: null
    };

    let formServiceRef = {
        sms: useRef(null),
        voice: useRef(null)
    };

    const [activeState, setActiveState] = useState(active);
    const [mergeAccountsModalShow, setMergeAccountsModalShow] = useState(false);
    const [mergeAccountsLoading, setMergeAccountsLoading] = useState(false);
    const [accountManagerLoading, setAccountManagerLoading] = useState(!account_manager_list.length);

    const [reportNumberChange, setReportNumberChange] = useState(formDefaultValue.report_number_change);

    const [supplierOptions, setSupplierOptions] = useState(
        {
            block_traffic_credit_excess: formDefaultValue?.block_traffic_credit_excess
        }
    );

    const formRef = useRef(null);
    const [chosenNickname, setChosenNickname] = useState(null);

    useEffect(() => {
        if (didFetchManagers.current || !newAccount) return;
        didFetchManagers.current = true;

        getAccountManagersDropdownList({});
    }, [newAccount, getAccountManagersDropdownList]);

    useEffect(() => {
        if (formRef.current) {
            formRef.current.checkForField("nickname");
        }
    }, [chosenNickname]);

    useEffect(() => {
        if (accountManagerLoading) {
            setTimeout(() => {
                setAccountManagerLoading(false);
            }, 3000)
        }
    }, [accountManagerLoading]);

    useEffect(() => {
        setActiveState(active);
    }, [active]);

    if (newSupplier && newAccount) {
        currentModel = supplierModelCreate;
    } else if (newSupplier && !newAccount) {
        currentModel = accountModelModify;
    } else if (!newAccount && !newSupplier && isSupplier) {
        currentModel = supplierModelModify;
    } else if (newAccount && !newSupplier) {
        if (!service) {
            currentModel = accountModelCreate;
        } else {
            currentModel = Schema.Model.combine(accountModelCreate, callDurationModel);
        }
    } else if (!newAccount && !newSupplier) {
        currentModel = accountModelModify;
    }

    const { users: formDefaultUsers, contacts: formDefaultContacts } = formDefaultValue || {};

    const formDefaultValueInit = JSON.parse(JSON.stringify(formDefaultValue));

    const formDefaultContactsInit = formDefaultContacts && formDefaultContacts.slice(0);
    const formDefaultUsersInit = formDefaultUsers && formDefaultUsers.slice(0);

    const [formState, setFormState] = useState(formDefaultValue || {});
    const getFormValue = (form) => form?.getFormValue?.() || formState || {};
    const [resellerAllowedValue, setResellerAllowedValue] = useState(formDefaultValue.reseller_allowed);
    const [reliableValue, setReliableValue] = useState(formDefaultValue.is_reliable);

    const [contacts, updateContacts] = useState(formDefaultContacts && formDefaultContacts.length ? formDefaultContacts : [{
        ...initContact,
        key: generateId()
    }]);

    // const makeAccountWithoutGenericFields = (object) => {
    //     return Object.keys(object).reduce((resultObject, key) => {
    //         if (["contacts", "users"].includes(key)) {
    //             const listWithGenericFields = object[key].map((item) => {
    //                 const resultItem = Object.keys(item).reduce((itemObject, itemKey) => {
    //                     if (itemKey !== "key")
    //                         itemObject[itemKey] = item[itemKey];

    //                     return itemObject;
    //                 }, {});

    //                 return resultItem;
    //             });

    //             resultObject[key] = listWithGenericFields;
    //         } else {
    //             resultObject[key] = object[key];
    //         }

    //         return resultObject;
    //     }, {});
    // };

    useEffect(() => {
        if (formDefaultContacts) {
            updateContacts(formDefaultContacts);
        }
    }, [formDefaultContacts]);

    useEffect(() => {
        if (!compareObjects(formDefaultValue, formState)) {
            setFormState(formDefaultValue);
        }
    }, [formDefaultValue])


    const windowWidth = useWindowWidth();
    // const isIssueWidth = windowWidth < 1600;
    const isMobile = windowWidth < 1350;

    const serviceValue = formDefaultValue?.service || null;

    // const newAccountService = formDefaultValue?.newAccountService;
    // const createOtherService = serviceListData.find(service => service.id !== serviceValue && serviceValue !== null);

    // const hasBothService = formDefaultValue?.joined_account_list
    //     ? !!formDefaultValue?.joined_account_list.find(acc => acc.service === createOtherService.id)
    //     : false;

    const accountServiceListData = useMemo(() => {
        return serviceListData.map(service => {
            const accountServiceData = accountJoinList && accountJoinList.find(account => account?.service === service.id) || {};
            // const accountOtherServiceData = accountJoinList && accountJoinList.find(account => account?.service !== service.id) || {};

            return {
                id: accountServiceData?.id,
                active: accountServiceData?.active,
                hasService: Object.keys(accountServiceData).length,
                service: service.id,
                pt_key: accountServiceData.pt_key,
                is_managed: accountServiceData.is_managed,
                service_name: service.name,
                currency_name: accountServiceData?.currency_name,
                payment_terms_name: accountServiceData?.payment_terms_name,
                account_manager_name: accountServiceData?.account_manager_name
            }
        });
    }, [accountJoinList]);

    useEffect(() => {
        if (!disabled && !contacts.length) {
            updateContacts(formDefaultContacts && formDefaultContacts.length ? formDefaultContacts : [{
                ...initContact,
                key: generateId()
            }]);
        }
    }, [formDefaultContacts, disabled]);

    const [users, updateUsers] = useState(formDefaultUsers || []);
    const [existDuplicates, updateExistDuplicates] = useState([]);

    const [showExpandedEmails, setShowExpandedEmails] = useState(false);
    const [errorContactMessages, setErrorContactMessages] = useState([]);

    // Иногда пропадают юзеры, точнее в state users не попадает ничего и он undefined. Разботает раз через 5 раз. Какой-то странный рандом. Данное решение фиксит этот баг
    useEffect(() => {
        if (!disabled && !users.length)
            updateUsers(formDefaultUsers || []);
    }, [formDefaultUsers, disabled]);

    const [showBanMessage, setShowBanMessage] = useState(false);

    useEffect(() => {
        setShowBanMessage(allocationBanExpiredDate);
    }, [allocationBanExpiredDate])

    useEffect(() => {
        if (account_manager_list.length) {
            setAccountManagerLoading(false);
        }
    }, [account_manager_list]);

    const [showConfirm, setShowConfirm] = useState(false);
    const [showCloseAccountConfirm, setShowCloseAccountConfirm] = useState(false);
    const [showResellerAllowConfirm, setShowResellerAllowConfirm] = useState(false);

    const formsContactRef = {};

    const updateContactFormRef = (ref, key) => {
        if (ref) {
            formsContactRef[key] = ref;
        }
    };

    const addContacts = () => {
        const unValidContacts = contacts.filter(contact => !formValid(managedContactModel.check(contact)));

        if (unValidContacts.length) {
            const localErrors = [...unValidContacts].reduce((acc, contact) => {
                const errorMessage = errorContactMessages.find(err => err.key === contact.key);

                acc.push({ ...errorMessage, key: contact.key, error: managedContactModel.check(contact) });
                return acc;
            }, []);

            localErrors.forEach(contact => {
                formsContactRef[contact.key].check();
            });

            setErrorContactMessages(localErrors);
        } else {
            updateContacts([...contacts, { ...initContact, key: generateId() }]);
        }
    };

    const contactTransform = (contactList) => {
        return contactList.map((contact) => {
            return {
                ...contact,
                teams_email: contact.teams_email || null,
                teams_link: contact.teams_link || null
            }
        });
    };

    const onSubmit = async () => {
        const validUsers =
            !Object.keys(formsUsersRef)
                .map(key => formsUsersRef[key].check())
                .filter(check => !check).length;

        let duplicateChecked = false;

        if (!newAccount && checkDuplicates) {
            const transformedContacts = contactTransform(contacts);
            duplicateChecked = await checkDuplicates(transformedContacts).then(({ telegram_username_duplicate_list: tgDupList }) => {
                if (tgDupList && tgDupList.length) {
                    const printDuplicateMessages = (id, name, type, accountName, service) => {
                        const tablePath = type === "supplier" ? "sms-suppliers" : "accounts";
                        const messenger = "Telegram";
                        return <>
                            {messenger} <b>{name}</b> exists in account <b>{accountName}
                                <LinkButton
                                    appearance="link"
                                    onClick={() => {
                                        window.open(`/${tablePath}/view/${id}?service=${service}&tab=general`);
                                    }}
                                >
                                    {service}
                                </LinkButton>
                            </b>
                            <br />
                        </>;
                    };

                    const alertContent = <AlertMessage>
                        {tgDupList.map((duplicate) => {
                            return printDuplicateMessages(
                                duplicate.account_id,
                                duplicate.telegram_username,
                                duplicate.type,
                                duplicate.account_name,
                                duplicate.service
                            );
                        })}
                    </AlertMessage>;

                    Alert.error(alertContent, 30000);

                    return true;
                } else {
                    updateExistDuplicates([]);
                    return false;
                }

            })
                .catch((error) => {
                    console.error("Error in checkDuplicates", error);
                });
        }

        if (!duplicateChecked) {
            const form = formRef.current;

            if (!form) {
                return;
            }

            form.check();

            const currentFormValue = getFormValue(form);
            const accountModelValues = currentModel.check(currentFormValue);
            const formCheck = hasErrorObject(accountModelValues);

            const unValidContacts = !newAccount ? contacts.filter(contact => !formValid(managedContactModel.check(contact))) : [];

            if (!unValidContacts.length && validUsers && !formCheck) {
                const formAccountManagerId = currentFormValue.account_manager_id;
                const accountManagerId = formAccountManagerId || formDefaultValue?.account_manager_id;
                let newUsers = [];
                let newContacts = [];

                if (!newAccount) {
                    newUsers = users;
                    newContacts = contacts;
                }

                let formValues = currentFormValue;

                formValues = {
                    ...formValues,
                    // hf_key: formValues.hf_key === null ? null : formValues.hf_key,
                    telegram_username: formValues.telegram_username === "" ? null : formValues.telegram_username,
                };

                if (reportNumberChange !== formDefaultValue.report_number_change) {
                    formValues = {
                        ...formValues,
                        report_number_change: reportNumberChange
                    };
                }

                if (reliableValue !== formDefaultValue.is_reliable) {
                    formValues = {
                        ...formValues,
                        is_reliable: reliableValue
                    };
                }

                if (isSupplier) {
                    formValues = {
                        ...formValues,
                        ...supplierOptions
                    };
                }

                const { user_teams_email, user_teams_link, ...restValues } = formValues;

                const requestData = {
                    ...restValues,
                    ...(user_teams_email ? { user_teams_email } : {}),
                    ...(user_teams_link ? { user_teams_link } : {})
                };

                const result = _onSubmit(new AccountCreate(requestData),
                    accountManagerId,
                    newUsers,
                    newContacts,
                    registration_key,
                    isSupplier
                );

                result && result.then(response => {
                    if (response && response.hasOwnProperty("result") && Object.keys(response["result"]).length) {
                        const localDuplicates = existDuplicates.filter(cnt => cnt.id === accountId).map(cnt => cnt.contact_id);

                        if (response["result"].hasOwnProperty("duplicate_list")) {
                            Alert.error("Some new entered messengers have duplicates ");

                            const resultDuplicateKeys = response["result"]["duplicate_list"].map(duplicate => duplicate.duplicate_id);

                            let currentDuplicates = localDuplicates.concat(resultDuplicateKeys);

                            updateExistDuplicates(currentDuplicates);
                            return;
                        } else if (localDuplicates.length >= existDuplicates.length && localDuplicates.length !== 0) {
                            Alert.error("Some new entered messengers have duplicates ");
                            return;
                        }

                        if (!isSupplier && formDefaultValue.reseller_allowed !== resellerAllowedValue) {
                            setResellerMode(accountId, resellerAllowedValue);
                        }

                        if (newAccount) {
                            getRegistrationCount();
                        }

                        Alert.success("Saved");
                    }

                    if (response && response?.code === 3) {
                        let errorMessage;

                        if (response?.data && Array.isArray(response.data)) {
                            errorMessage = response.data.join("\n");
                        } else {
                            errorMessage = response.data;
                        }

                        Alert.error(errorMessage);
                    }

                })
            } else {

                if (unValidContacts.length) {
                    const localErrors = [...unValidContacts].reduce((acc, contact) => {
                        const errorMessage = errorContactMessages.find(err => err.key === contact.key);

                        acc.push({ ...errorMessage, key: contact.key, error: managedContactModel.check(contact) })
                        return acc;
                    }, []);

                    localErrors.forEach(contact => {
                        const contactForm = formsContactRef[contact.key];
                        contactForm.check();
                    });

                    setErrorContactMessages(localErrors);
                }
            }
        }
    };

    const formsUsersRef = {};
    const updateUsersFormRef = (ref, key) => formsUsersRef[key] = ref;

    const onAddUser = () => {
        const unValidUsers = users.filter(user => !formValid(userModel.check(user)) && !user.email_disabled);

        if (unValidUsers.length)
            unValidUsers.map(
                user => formsUsersRef[user.key].check()
            );
        else
            updateUsers([{ ...initUser, key: generateId() }, ...users]);
    };

    const fieldIsChanged = (key) => {
        return formDefaultValueInit.hasOwnProperty(key)
            ? formState && !!formState[key] && formDefaultValueInit[key] !== formState[key]
            : formState && !!formState[key];
    };

    // const radioIsPicked = (key, radioKey) => {
    //     return fieldIsChanged(key) && formState[key] === radioKey;
    // };

    const addableIsChanged = (id, addableObjectInitial, addableObject) => {
        const addedNew = addableObjectInitial && !addableObjectInitial.map((addable) => addable.id).includes(id);
        const changed = addableObjectInitial && !compareObjects(addableObjectInitial.find(obj => obj.id === id), addableObject);
        return addedNew || changed;
    };

    const onChangeActivity = async (activeState, callback) => {
        const currentSupplierId = currentSupplier.supplier_id;

        const method = activeState ? "sms.supplier:remove" : "sms.supplier:restore";
        const resultMessage = activeState ? "Supplier account was closed" : "Supplier account was opened";

        await api(method, {
            target: { "sms.supplier_id": currentSupplierId }
        }).then((result) => {

            if (result["sms.supplier"]) {
                Alert.success(resultMessage);

                updateCurrentPage();
                setActiveState(!activeState);
            }
        }).finally(() => {
            setShowCloseAccountConfirm(false);
        });
    };

    const updateCurrentPage = () => {
        history.replace("/reload");
        setTimeout((valueLocation, valueHistory) => {
            valueHistory.replace({
                ...valueLocation,
                search: "?tab=general"
            });
        }, 100, location, history);
    };

    const accountManagerList = account_manager_list.filter(value => value.active);
    const managedServiceList = getManagedServices(authInfo);
    // const disabledServices = serviceListData.filter(service => !managedServiceList.includes(service.id)).map(service => service.id);

    const isAnotherServiceExist = !!accountServiceListData.find(accountService => accountService.service !== formDefaultValue?.service)?.id;

    const isGeneratedName = !!generatedNameList.length;

    const showSaveCloseButton = !isSupervisorSms || isSupervisorSms && checkPermissionsFor(SMS_SUPPLIER_MODIFY_METHOD);

    const userNameLabel = newSupplier ? "Contact Name" : "User Name";
    const userEmailLabel = newSupplier ? "Contact Email" : "User Email";
    const userTelegramLabel = newSupplier ? "Contact Telegram" : "User Telegram";

    const userTeamsEmailLabel = newSupplier ? "Contact Teams Email" : "User Teams Email";
    const userTeamsLinkLabel = newSupplier ? "Contact Teams Link" : "User Teams Link";

    const disableEditButtons = !checkPermissionsFor(isSupplier ? SMS_SUPPLIER_MODIFY_METHOD : ACCOUNT_MODIFY_METHOD);

    return (<>
        {
            showExpandedEmails &&
            ReactDOM.createPortal(
                <ExpandedEmailsPortal className="portal" onClick={() => setShowExpandedEmails(false)} />
                , document.body
            )
        }
        <Form
            model={currentModel}
            style={{ marginBottom: "20px" }}
            layout="horizontal"
            ref={formRef}
            formValue={formState}
            onChange={setFormState}
            formDefaultValue={formDefaultValue}
        >
            <FlexboxGrid>
                <StyledFlexboxGridItem>
                    <FlexboxGrid style={styleRow}>
                        <StyledRow column isMobile={isMobile}>
                            <StyledFlexboxGridItem row={!isMobile}>
                                <FormField
                                    style={{ minWidth: "250px" }}
                                    disabled={disabled}
                                    accepter={Input}
                                    name="name"
                                    value={formState.name}
                                    placeholder="Account name"
                                    label="Account name"
                                    errorPlacement="topStart"
                                    className={`tab-general-custom-field ${fieldIsChanged("name") && "has-changed"}`}
                                />
                            </StyledFlexboxGridItem>
                            {!isSupplier && <StyledFlexboxGridItem row={!isMobile}>
                                <FormField
                                    style={{ minWidth: "250px" }}
                                    disabled={disabled}
                                    accepter={Input}
                                    name="nickname"
                                    onChange={() => {
                                        setChosenNickname(null);
                                    }}
                                    value={formState.nickname || chosenNickname}
                                    placeholder="Account nickname"
                                    label="Account nickname"
                                    errorPlacement="topStart"
                                    className={`tab-general-custom-field ${fieldIsChanged("nickname") && "has-changed"}`}
                                />
                                <GeneratedNamesContainer show={newAccount && isGeneratedName && isMobile || newAccount && isGeneratedName && !isMobile}>
                                    {newAccount && isGeneratedName
                                        ?
                                        <ButtonToolbar>
                                            <Whisper
                                                // open={newAccount && !formState.name && !chosenNickname}
                                                placement="topLeft"
                                                trigger="hover"
                                                speaker={<Popover>You have the option to choose a name from the buttons on the right.</Popover>}
                                            >
                                                <GeneratedNamesButtonGroup>
                                                    {generatedNameList.map((nickname) => {
                                                        const chosen = chosenNickname === nickname;
                                                        const chosenColor = service ? "violet" : "blue";
                                                        const chosenAppearance = chosen ? "primary" : "ghost";

                                                        return <GeneratedNameButton
                                                            color={chosenColor}
                                                            appearance={chosenAppearance}
                                                            onClick={() => {
                                                                setChosenNickname(nickname);
                                                                setFormState((currentFormState) => {
                                                                    const newFormState = {
                                                                        ...currentFormState,
                                                                        nickname
                                                                    };

                                                                    return newFormState;
                                                                });
                                                            }}
                                                        >
                                                            {nickname}
                                                        </GeneratedNameButton>
                                                    })}
                                                </GeneratedNamesButtonGroup>
                                            </Whisper>
                                        </ButtonToolbar> : <></>
                                    }
                                </GeneratedNamesContainer>
                            </StyledFlexboxGridItem>}
                            <StyledFlexboxGridItem row={!isMobile}>
                                {newAccount && <FormField
                                    style={{ minWidth: "250px" }}
                                    disabled={disabled}
                                    accepter={SelectPicker}
                                    placeholder="Account manager"
                                    data={accountManagerList}
                                    labelKey="name"
                                    valueKey="id"
                                    name="account_manager_id"
                                    label="Account manager"
                                    searchable={false}
                                    cleanable={false}
                                    className={`tab-general-select-field ${fieldIsChanged("account_manager_id") && "has-changed"}`}
                                />}

                                {(newAccount || isSupplier) && <FormField
                                    style={{ minWidth: "250px" }}
                                    accepter={SelectPicker}
                                    data={payment_terms_list}
                                    label="Payment Terms"
                                    labelKey="name"
                                    valueKey="pt_key"
                                    name="pt_key"
                                    searchable={false}
                                    cleanable={false}
                                    errorPlacement="topEnd"
                                    className={`tab-general-select-field ${fieldIsChanged("pt_key") && "has-changed"}`}
                                />}

                                {isSupplier && !newAccount && <SelfUpdateDropdownField
                                    disabled={disabled}
                                    updateOn={accountManagerLoading}
                                    data={accountManagerList}
                                    labelKey="name"
                                    valueKey="id"
                                    name="account_manager_id"
                                    placeholder="Account manager"
                                    label="Account manager"
                                    searchable={false}
                                    cleanable={false}
                                    className={`tab-general-select-field ${fieldIsChanged("account_manager_id") && "has-changed"}`}
                                />}

                                {(newAccount || isSupplier) && <FormField
                                    style={{ minWidth: "250px" }}
                                    disabled={disabled || disabledCurrency}
                                    accepter={SelectPicker}
                                    name="cur_key"
                                    placeholder="Currency"
                                    data={currency_list}
                                    labelKey="name"
                                    valueKey="cur_key"
                                    label="Currency"
                                    searchable={false}
                                    cleanable={false}
                                    className={`tab-general-select-field ${fieldIsChanged("cur_key") && "has-changed"}`}
                                />}

                                {service && newAccount && !newSupplier && <FormField
                                    disabled={disabled}
                                    accepter={Input}
                                    name="call_duration_max"
                                    placeholder="2400"
                                    label="Call duration"
                                    className={`tab-general-custom-field ${fieldIsChanged("call_duration_max") && "has-changed"}`}
                                />}


                                {(newSupplier || isSupplier) && <FormField
                                    align="center"
                                    label="Credit limit"
                                    name="supplier_credit_limit"
                                    placeholder="0"
                                    className={`tab-general-custom-field ${fieldIsChanged("supplier_credit_limit") && "has-changed"}`}
                                />}
                            </StyledFlexboxGridItem>
                            <StyledFlexboxGridItem row={!isMobile}>
                                {(newAccount || newSupplier) &&
                                    <FormField
                                        style={{ minWidth: "250px" }}
                                        disabled={disabled}
                                        accepter={Input}
                                        name="user_name"
                                        placeholder={userNameLabel}
                                        label={userNameLabel}
                                        errorPlacement="topStart"
                                        className={`tab-general-custom-field ${fieldIsChanged("name") && "has-changed"}`}
                                    />}
                                {(newAccount || newSupplier) &&
                                    <FormField
                                        style={{ minWidth: "250px" }}
                                        disabled={disabled}
                                        accepter={Input}
                                        name="user_email"
                                        placeholder={userEmailLabel}
                                        label={userEmailLabel}
                                        errorPlacement="topStart"
                                        className={`tab-general-custom-field ${fieldIsChanged("name") && "has-changed"}`}
                                    />}
                                {(newAccount || newSupplier) &&
                                    <FormField
                                        style={{ minWidth: "250px" }}
                                        disabled={disabled}
                                        accepter={Input}
                                        name="telegram_username"
                                        errorPlacement="topStart"
                                        label={userTelegramLabel}
                                        placeholder={userTelegramLabel}
                                        className={`tab-general-custom-field ${fieldIsChanged("user_telegram") && "has-changed"}`}
                                    />}
                            </StyledFlexboxGridItem>
                            <StyledFlexboxGridItem row={!isMobile}>
                                {(newAccount || newSupplier) &&
                                    <FormField
                                        style={{ minWidth: "250px" }}
                                        disabled={disabled}
                                        accepter={Input}
                                        name="user_teams_email"
                                        placeholder={userTeamsEmailLabel}
                                        label={userTeamsEmailLabel}
                                        errorPlacement="topStart"
                                        className={`tab-general-custom-field ${fieldIsChanged("user_teams_email") && "has-changed"}`}
                                    />}
                                {(newAccount || newSupplier) &&
                                    <FormField
                                        style={{ minWidth: "250px" }}
                                        disabled={disabled}
                                        accepter={Input}
                                        name="user_teams_link"
                                        placeholder={userTeamsLinkLabel}
                                        label={userTeamsLinkLabel}
                                        errorPlacement="topStart"
                                        className={`tab-general-custom-field ${fieldIsChanged("user_teams_link") && "has-changed"}`}
                                    />}
                            </StyledFlexboxGridItem>
                            <StyledFlexboxGridItem row={!isMobile}>
                                {!isSupplier && <FormField
                                    style={{ minWidth: "250px" }}
                                    disabled={disabled}
                                    data={countryList}
                                    labelKey="name"
                                    valueKey="cntr_code"
                                    accepter={SelectPicker}
                                    name="cntr_code"
                                    placeholder="Country"
                                    label="Country"
                                    className={`tab-general-custom-field ${fieldIsChanged("cntr_code") && "has-changed"}`}
                                />}
                                {(newAccount && !isSupplier || !isSupplier) && <FormField
                                    style={{ minWidth: "250px" }}
                                    disabled={disabled}
                                    data={howFindList}
                                    labelKey="how_find_name"
                                    valueKey="hf_key"
                                    accepter={SelectPicker}
                                    name="hf_key"
                                    placeholder="Choose an option"
                                    label="How did you find us?"
                                    className={`tab-general-custom-field ${fieldIsChanged("hf_key") && "has-changed"}`}
                                />}
                            </StyledFlexboxGridItem>
                        </StyledRow>
                    </FlexboxGrid>
                </StyledFlexboxGridItem>
                <StyledFlexboxGridItem>
                    <StyledFlexboxGrid column>
                        {!newSupplier && !isSupplier ? <StyledFlexboxGridItem column>
                            {!newAccount && <ServiceTable
                                formServiceRef={formServiceRef}
                                formState={formState}
                                accountId={accountId}
                                data={accountServiceListData}
                                service={service}
                                history={history}
                                location={location}
                                authInfo={authInfo}
                                isMobile={isMobile}
                                isCreateJoined={isCreateJoined}
                                loading={joinLoading || mergeAccountsLoading}
                                setService={setService}
                                currencyList={currency_list}
                                getAccountJoin={getAccountJoin}
                                paymentTermsList={payment_terms_list}
                                accountManagerList={accountManagerList}
                            />}

                            {showBanMessage && <BanInfoContainer>
                                <BanAccountInfo>Account made to many allocation attempts and was limited till {<DateItem>{getFormattedDatetime(allocationBanExpiredDate)}</DateItem>}</BanAccountInfo>
                                <BanCleanButton
                                    appearance="primary"
                                    onClick={() => {
                                        setShowConfirm(true);
                                    }}
                                >
                                    Remove allocation block
                                </BanCleanButton>
                            </BanInfoContainer>}
                        </StyledFlexboxGridItem> : <></>}
                        {!newAccount && !newSupplier && isSupplier ? <StyledFlexboxGridItem column>
                            <StyledSupplierOptionsContainer>
                                <ControlledCheckbox
                                    name="block_traffic_credit_excess"
                                    defaultValue={supplierOptions?.block_traffic_credit_excess}
                                    onChange={(value) => {
                                        const localSupplierOptions = { ...supplierOptions };

                                        setSupplierOptions({
                                            ...localSupplierOptions,
                                            block_traffic_credit_excess: value
                                        });
                                    }}
                                >
                                    Block traffic if credit limit exceed
                                </ControlledCheckbox>
                            </StyledSupplierOptionsContainer>
                        </StyledFlexboxGridItem> : <></>}
                        <StyledFlexboxGridItem column>
                            {!isSupplier && <AccountSettings isMobile={isMobile}>
                                <FlexboxGrid.Item>
                                    {!newAccount && <ServiceSettings
                                        value={resellerAllowedValue}
                                        setValue={(value) => {
                                            if (value) {
                                                setFormState((currentFormValue) => {
                                                    return {
                                                        ...currentFormValue,
                                                        reseller_allowed: true
                                                    };
                                                });
                                                setResellerAllowedValue(true);
                                            } else {
                                                setShowResellerAllowConfirm(true);
                                            }
                                        }}
                                        disabled={disabled}
                                        defaultValue={formState.reseller_allowed}
                                    />}
                                </FlexboxGrid.Item>
                                <FlexboxGrid.Item>
                                    {!newAccount && <Checkbox
                                        name="report_number_change"
                                        onChange={(_, checked) => setReportNumberChange(checked)}
                                        checked={reportNumberChange}
                                        disabled={disabled}
                                        defaultChecked={formDefaultValue.report_number_change}
                                    >
                                        Send changes in allocated numbers
                                    </Checkbox>}
                                </FlexboxGrid.Item>
                                {!newAccount && <FlexboxGrid.Item>
                                    <Checkbox
                                        name="is_reliable"
                                        onChange={(_, checked) => setReliableValue(checked)}
                                        checked={reliableValue}
                                        defaultChecked={formDefaultValue.is_reliable}
                                        disabled={!isSupport || disabled}
                                    >
                                        Is trusted
                                    </Checkbox>
                                </FlexboxGrid.Item>}
                            </AccountSettings>}
                        </StyledFlexboxGridItem>
                    </StyledFlexboxGrid>
                </StyledFlexboxGridItem>
                {!newAccount && !newSupplier && !isSupplier && checkPermissionsFor("account:merge") && <StyledFlexboxGridItem extraMargin={"14px 0 0 0"}>
                    <>
                        <MergeAccountsHeader>Merge accounts</MergeAccountsHeader>
                        <Whisper
                            placement="auto"
                            trigger="hover"
                            hide={!isAnotherServiceExist}
                            speaker={<Popover>You have both account services</Popover>}
                        >
                            <MergeAccountsButton
                                disabled={isAnotherServiceExist || mergeAccountsLoading || disabled}
                                onClick={() => {
                                    setMergeAccountsModalShow(true);
                                }}
                            >
                                Merge accounts
                            </MergeAccountsButton>
                        </Whisper>
                    </>
                </StyledFlexboxGridItem>}
            </FlexboxGrid>
            {newAccount || isSeparatedPage && !checkPermissionsFor("account_contact:create")
                ? null
                : <>
                    <FlexboxGrid style={styleRow}>
                        <HeaderList>Contacts <ButtonListAdd padding={4} disabled={disabled} onClick={addContacts} /> </HeaderList>
                    </FlexboxGrid>

                    <FlexboxGrid style={styleRow}>
                        <Contacts
                            {...{
                                active, contacts, updateContacts, disabled, addableIsChanged,
                                showExpandedEmails, setShowExpandedEmails, errorContactMessages,
                                accountServiceListData
                            }}
                            conflictContacts={existDuplicates}
                            formsRef={formsContactRef}
                            updateFormRef={updateContactFormRef}
                            initialList={formDefaultContactsInit}
                            isMobile={isMobile}
                            isSupplier={isSupplier}
                        />
                    </FlexboxGrid>
                </>
            }


            {isSupplier || (newAccount || isSeparatedPage && !checkPermissionsFor("account_user:create"))
                ? null
                : <>
                    <FlexboxGrid style={styleRow}>
                        <HeaderList>Customers <ButtonListAdd padding={4} disabled={disabled} onClick={onAddUser} /> </HeaderList>
                    </FlexboxGrid>

                    <FlexboxGrid style={styleRow}>
                        <Users
                            {...{
                                users,
                                updateUsers,
                                active,
                                disabled: disabled || disableEditButtons,
                                addableIsChanged
                            }}
                            clientRoleList={client_role_list || []}
                            adminRoleList={admin_role_list || []}
                            isTestAccount={isTestAccount}
                            formsRef={formsUsersRef}
                            updateFormRef={updateUsersFormRef}
                            initialList={formDefaultUsersInit}
                            isMobile={isMobile}
                        />
                    </FlexboxGrid>
                </>
            }

            <ButtonFooter>

                {showSaveCloseButton && <ButtonPrimary
                    disabled={disabled || disableEditButtons}
                    onClick={onSubmit}
                >
                    Save
                </ButtonPrimary>}

                <ButtonContent
                    onClick={() => onCancel()}
                >
                    Cancel
                </ButtonContent>

                {(!newSupplier && !newAccount) && isSupplier && showSaveCloseButton && <>
                    {activeState
                        ? <ButtonClose
                            onClick={() => {
                                setShowCloseAccountConfirm(true);
                            }}
                        >
                            Close account <Icon icon="lock" style={{ marginLeft: 5 }} />
                        </ButtonClose>
                        : <ButtonSecondary
                            onClick={() => {
                                onChangeActivity(activeState);
                            }}
                        >
                            Open account <Icon icon="unlock" style={{ marginLeft: 5 }} />
                        </ButtonSecondary>
                    }
                </>
                }

            </ButtonFooter>
        </Form>
        {mergeAccountsModalShow && <MergeAccountsModal
            show={mergeAccountsModalShow}
            onClose={() => {
                setMergeAccountsModalShow(false);
            }}
            updateCurrentPage={updateCurrentPage}
            accountService={formDefaultValue?.service}
            accountId={accountId}
            loading={mergeAccountsLoading}
            setLoading={setMergeAccountsLoading}
        />}

        <ModalConfirm
            show={showConfirm}
            onClose={() => {
                setShowConfirm(false);
            }}
            onSuccess={() => {
                api(ACCOUNT_ALLOCATION_BAN_CLEAN_API, { target: { account_id: accountId } }).then((response) => {
                    if (response?.result === "OK") {
                        Alert.success("Ban has been removed", 3000);
                        setShowBanMessage(false);
                        setShowConfirm(false);
                    }
                });
            }}
            title="Remove allocation block"
        >Are you sure?</ModalConfirm>
        <ModalConfirm
            show={showCloseAccountConfirm}
            onClose={() => {
                setShowCloseAccountConfirm(false);
            }}
            onSuccess={() => {
                onChangeActivity(activeState);
            }}
            title="Close account"
        >
            <p>You are sure?</p>
            <p>All numbers will revoke and all trunks remove</p>
        </ModalConfirm>
        <ModalConfirm
            show={showResellerAllowConfirm}
            onClose={() => {
                setShowResellerAllowConfirm(false);
            }}
            onSuccess={() => {
                setFormState((currentFormValue) => {
                    return {
                        ...currentFormValue,
                        reseller_allowed: false
                    }
                })

                setResellerAllowedValue(false);
                setShowResellerAllowConfirm(false);
            }}
            title="Disallow reseller"
        >
            <p>You are sure?</p>
            <p>All numbers will revoke and all trunks remove</p>
        </ModalConfirm>
    </>);
};


const SelfUpdateDropdownField = ({
    disabled,
    updateOn,
    data,
    labelKey,
    valueKey,
    name,
    label,
    placeholder = "List...",
    searchable = false,
    cleanable = false,
    className = "",
    ...props
}) => {
    const [update, setUpdate] = useState(false);

    useEffect(() => {
        setUpdate(updateOn);
    }, [updateOn]);

    return !update ? <FormField
        style={{ minWidth: "250px" }}
        disabled={disabled || updateOn}
        accepter={SelectPicker}
        placeholder={placeholder}
        data={data}
        labelKey={labelKey}
        valueKey={valueKey}
        name={name}
        label={label}
        searchable={searchable}
        cleanable={cleanable}
        errorPlacement="topEnd"
        className={className}
    /> : <FormField
        style={{ minWidth: "250px" }}
        disabled={updateOn}
        placeholder={placeholder}
        label={label}
        className={className}
    />;
};

const ServiceSettings = ({
    value,
    setValue,
    disabled,

    defaultValue = false
}) => {
    return <>
        <Checkbox
            name="reseller_allowed"
            onChange={(_, checked) => setValue(checked)}
            checked={value}
            disabled={disabled}
            defaultChecked={defaultValue}
        >
            Allow reseller panel
        </Checkbox>
    </>;
};

const FormField = ({ ...props }) => {
    return <CustomField
        {...props}
        formGroupExtraStyle={{
            marginBottom: 10,
            marginRight: 30
        }}
    />;
};

const StyledFlexboxGrid = styled(FlexboxGrid)`
    && {
        ${props => props.column && css`
            flex-direction: column;
        `}
    }
`;

const StyledFlexboxGridItem = styled(FlexboxGrid.Item)`
    /* margin-right: 40px; */
    ${props => props.row && css`
        display: flex;
        flex-direction: row;
    `}
    margin: ${props => props.extraMargin || 0};
`;

const BanInfoContainer = styled.div`
    display: flex;
    flex-direction: column;
`;

const BanAccountInfo = styled.span`
    color: red;
`;

const BanCleanButton = styled(Button)`
    &&& {
        background-color: var(--color-warning) !important;
        margin-top: 10px;
        width: 200px
    }
`;

const StyledRow = styled.div`
    display: flex;
    flex-direction: ${props => props.isMobile || props.column ? "column" : "row"};
    width: 100%;
    padding: 5px 10px 5px 10px !important;
    flex-wrap: wrap;
`;

const DateItem = styled.span`
    && {
        font-weight: 600;
    }
`;

const AlertMessage = styled.p`
  text-align: justify;
`;

const LinkButton = styled(Button)`
  padding: 0 5px 0 5px !important; 
`;

const HeaderList = styled.div`
    font-family: Roboto;
    font-style: normal;
    font-weight: 500;
    font-size: 18px;
    line-height: 21px;
    /* identical to box height */
    color: var(--color-main);
`;

const ButtonFooter = styled.div`
    text-align: center;
    margin-top: 30px;
    > .rs-btn {
        margin-right: 50px
    }
`;

const ExpandedEmailsPortal = styled.div`
    width: 100%;
    height: ${() => document.body.offsetHeight - 56}px;
    top: 56px;
    left: 0;
    position: absolute;
    z-index: 5;
`;

const MergeAccountsHeader = styled.label`
    && {
        min-height: 20px;
        // line-height: 1.42857143;
        font-size: 14px;
        padding: 8px 0;
        width: 170px;
        margin-right: 12px;
        margin-bottom: 4px;


        font-family: Roboto;
        font-style: normal;
        font-weight: 500;
        font-size: 18px;
        line-height: 21px;
        /* identical to box height */
        color: var(--color-main);
    }
`;

const MergeAccountsButton = styled(Button)`
    && {
        display: flex;
    }
`;

const StyledSupplierOptionsContainer = styled.div`
    && {
        margin-top: 40px;
    }
`;

const AccountSettings = styled(FlexboxGrid)`
    && {
        position: relative;

        ${props => props.isMobile ? css`
            flex-direction: column;
        ` : css`
            /* left: 585px; */
            width: fit-content;
        `}
    }

`;

const GeneratedNamesContainer = styled.div`
    && {
        height: 86px;
        display: ${props => props.show ? "flex" : "none"};
        justify-content: center;
        align-items: center;
    }
`;

const GeneratedNameButton = styled(Button)`
    
`;

const GeneratedNamesButtonGroup = styled(ButtonGroup)`
    && {
        margin-top: 27px;
    }
`;
